import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// River gauges on a saved site (Stage 4). Observed readings (USGS,
// provisional), the gauge's official NWS flood stages, NWS's own official
// category, and the official river forecast are labelled separately —
// observed vs forecast, official vs calculated.

const LEVEL_SEV = { none: 0, action: 1, minor: 2, moderate: 3, major: 3 };
const OFFICIAL_LABEL = {
  no_flooding: 'No flooding',
  action: 'Action stage',
  minor: 'Minor flooding',
  moderate: 'Moderate flooding',
  major: 'Major flooding',
  fcst_not_current: 'No current forecast',
  not_defined: 'Flood stages not defined',
  obs_not_current: 'Observation not current',
  out_of_service: 'Out of service',
};

function fmt(iso, timeZone) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone || undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/** Horizontal scale: current stage against the gauge's flood stages. */
function StageScale({ thresholds, stageFt, crestFt }) {
  if (!thresholds) return null;
  const marks = ['action', 'minor', 'moderate', 'major'].filter((l) => thresholds[l] != null);
  const values = [...marks.map((l) => thresholds[l]), stageFt, crestFt].filter((v) => v != null);
  const lo = Math.min(...values) - 2;
  const hi = Math.max(...values) + 2;
  const pct = (v) => `${((v - lo) / (hi - lo)) * 100}%`;
  return (
    <div className="stage-scale" aria-label="Stage relative to flood stages">
      {marks.map((l) => (
        <span key={l} className={`stage-mark mark-${l}`} style={{ left: pct(thresholds[l]) }} title={`${l} ${thresholds[l]} ft`}>
          <span>{l[0].toUpperCase() + l.slice(1)} {thresholds[l]}</span>
        </span>
      ))}
      {crestFt != null && <span className="stage-dot crest" style={{ left: pct(crestFt) }} title={`Forecast crest ${crestFt} ft`} />}
      {stageFt != null && <span className="stage-dot now" style={{ left: pct(stageFt) }} title={`Now ${stageFt} ft`} />}
    </div>
  );
}

function GaugeCard({ g, timeZone, onRemove }) {
  if (g.error) {
    return (
      <div className="rule sev-1">
        <div className="rule-title">{g.key}</div>
        <div className="rule-detail">{g.error}</div>
      </div>
    );
  }
  const o = g.observed;
  const f = g.forecast;
  const usgsDown = g.feeds?.usgs?.status === 'unavailable';
  const sev = o?.level ? LEVEL_SEV[o.level] : 0;
  return (
    <div className={`rule gauge-card sev-${sev}`}>
      <div className="rule-head">
        <span className="rule-title">{g.name}</span>
        <span className="rule-cite">
          {g.lid ? `NWS ${g.lid}` : ''}
          {g.usgsId ? ` · USGS ${g.usgsId}` : ''}
          {g.distanceKm != null ? ` · ${g.distanceKm} km from site` : ''}
        </span>
      </div>
      {!g.inService && <div className="rule-detail feed-note">Out of service{g.inServiceMessage ? `: ${g.inServiceMessage}` : ''}</div>}

      <div className="gauge-grid">
        <div>
          <div className="gauge-k">Observed now</div>
          {o ? (
            <>
              <div className="gauge-v">
                {o.stageFt.toFixed(2)} ft{o.flowCfs != null ? ` · ${Math.round(o.flowCfs).toLocaleString()} cfs` : ''}
              </div>
              <div className="rule-tags">
                <span className="tag tag-forecast">Observed · USGS</span>
                {o.provisional && <span className="tag" title="Recent USGS data is provisional and may be revised">Provisional</span>}
                {o.stale && <span className="tag tag-planning">Stale reading</span>}
                {o.rateOfRise && (
                  <span className="tag">
                    {o.rateOfRise.ftPerHour > 0 ? '▲' : o.rateOfRise.ftPerHour < 0 ? '▼' : '■'} {Math.abs(o.rateOfRise.ftPerHour)} ft/hr
                    over {o.rateOfRise.overHours} h
                  </span>
                )}
              </div>
              <div className="rule-detail">
                {o.levelLabel ? <strong>{o.levelLabel}</strong> : 'No NWS flood stages for this gauge'} · {fmt(o.at, timeZone)}
              </div>
            </>
          ) : (
            <div className="rule-detail feed-note">
              {usgsDown
                ? 'USGS observations unavailable — this is not an all-clear.'
                : g.usgsId
                  ? 'No recent readings.'
                  : 'No USGS gauge linked to this NWS point.'}
            </div>
          )}
        </div>

        <div>
          <div className="gauge-k">
            <span className="badge-official">Official</span> NWS category
          </div>
          <div className="gauge-v">{OFFICIAL_LABEL[g.official?.observedCategory] ?? g.official?.observedCategory ?? '—'}</div>
          <div className="rule-detail">
            {g.official?.observedStageFt != null ? `${g.official.observedStageFt} ft · ` : ''}
            {fmt(g.official?.observedAt, timeZone)}
          </div>
        </div>

        <div>
          <div className="gauge-k">
            <span className="badge-official">Official</span> River forecast
          </div>
          {f ? (
            <>
              <div className="gauge-v">
                Crest {f.crestFt.toFixed(1)} ft · {fmt(f.crestAt, timeZone)}
              </div>
              <div className="rule-tags">
                <span className="tag tag-forecast">Forecast · NWS</span>
                {f.levelLabel && <span className={`tag ${LEVEL_SEV[f.crestLevel] >= 2 ? 'tag-planning' : ''}`}>{f.levelLabel} at crest</span>}
              </div>
              <div className="rule-detail">
                {f.firstMinorFloodAt ? `Reaches minor flood stage ${fmt(f.firstMinorFloodAt, timeZone)} · ` : ''}
                Issued {fmt(f.issuedAt, timeZone)}
              </div>
            </>
          ) : (
            <div className="rule-detail">
              No current forecast.{g.forecastReliability ? ` ${g.forecastReliability}` : ''}
            </div>
          )}
        </div>
      </div>

      <StageScale thresholds={g.thresholds} stageFt={o?.stageFt ?? null} crestFt={f?.crestFt ?? null} />
      {!g.thresholds && (
        <div className="rule-detail muted">
          This gauge has no NWS-defined flood stages, so no flood level is shown — readings only.
        </div>
      )}

      <div className="rule-detail alert-links">
        {g.links?.nwps && (
          <a href={g.links.nwps} target="_blank" rel="noreferrer">
            NWS hydrograph
          </a>
        )}
        {g.links?.usgs && (
          <a href={g.links.usgs} target="_blank" rel="noreferrer">
            USGS site
          </a>
        )}
        {onRemove && (
          <button type="button" className="btn-link" onClick={onRemove}>
            Remove from site
          </button>
        )}
      </div>
    </div>
  );
}

function GaugePicker({ siteId, onAdd, onClose }) {
  const [radius, setRadius] = useState(25);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setList(null);
    setError(null);
    api(`/sites/${siteId}/gauges/nearby?radiusKm=${radius}`)
      .then(setList)
      .catch((err) => setError(err.message));
  }, [siteId, radius]);

  return (
    <div className="form-card">
      <div className="official-panel-head">
        <h3>Add a gauge</h3>
        <button type="button" className="btn-link" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="muted form-hint">
        Nearest isn&rsquo;t always right. Gauges on the site&rsquo;s own stream (from the USGS stream network) are listed
        first — usually the one just upstream matters most. Check its NWS hydrograph before relying on it.
      </p>
      <label className="industry-select">
        Within
        <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
          {[10, 25, 50, 100].map((r) => (
            <option key={r} value={r}>
              {r} km
            </option>
          ))}
        </select>
      </label>
      {error && <div className="banner err">{error}</div>}
      {!list && !error && <div className="rule-detail">Searching NWS gauges…</div>}
      {list?.length === 0 && <div className="rule-detail">No NWS gauges within {radius} km.</div>}
      {list?.map((g) => (
        <div key={g.lid} className="gauge-pick">
          <div>
            <strong>{g.name}</strong>
            {g.relationLabel && (
              <span
                className={`tag ${['same-reach', 'upstream-mainstem', 'downstream'].includes(g.relation) ? 'tag-forecast' : ''}`}
                style={{ marginLeft: 8 }}
                title="From the USGS stream network (NLDI) around the site"
              >
                {g.relationLabel}
              </span>
            )}
            <span className="place">
              {g.lid} · {g.distanceKm} km{g.hasForecastPoint ? ' · forecast point' : ''}
              {g.observedStageFt != null ? ` · ${g.observedStageFt} ft now` : ''}
            </span>
          </div>
          <div className="form-actions">
            <a className="btn-link" href={g.pageUrl} target="_blank" rel="noreferrer">
              Hydrograph
            </a>
            {g.selected ? (
              <span className="muted">Added</span>
            ) : (
              <button type="button" className="btn" onClick={() => onAdd(g)}>
                Add
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Gauges section on the site page. */
export default function SiteGauges({ site, timeZone, onSiteChange }) {
  const [statuses, setStatuses] = useState(null);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatuses(await api(`/sites/${site.id}/gauges`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [site.id]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => clearInterval(id);
  }, [load, site.gauges]);

  async function saveGauges(gauges) {
    try {
      const updated = await api(`/sites/${site.id}`, { method: 'PATCH', body: { gauges } });
      onSiteChange?.(updated);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section>
      <div className="official-panel-head" style={{ marginTop: 22 }}>
        <h3>River gauges</h3>
        {!picking && (
          <button type="button" className="btn" onClick={() => setPicking(true)}>
            Add gauge
          </button>
        )}
      </div>
      {error && <div className="banner err">{error}</div>}
      {picking && (
        <GaugePicker
          siteId={site.id}
          onClose={() => setPicking(false)}
          onAdd={(g) => saveGauges([...(site.gauges ?? []), { provider: 'NWPS', id: g.lid, label: g.name }])}
        />
      )}
      {site.gauges?.length === 0 && !picking && (
        <div className="rule-detail">
          No gauges selected. Add the gauge(s) that matter for this site to see observed stage, official NWS flood
          stages and river forecasts, and to record flood episodes in the site&rsquo;s history.
        </div>
      )}
      {statuses?.map((g) => (
        <GaugeCard
          key={g.key}
          g={g}
          timeZone={timeZone}
          onRemove={() => saveGauges(site.gauges.filter((x) => `${x.provider}:${x.id}` !== g.key))}
        />
      ))}
    </section>
  );
}
